import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { RemovePetButton } from '../../components/common/buttons/RemovePetButton.tsx'
import { createPetAndAssignTracker, parseBirthDate } from '../../helpers/petHelper.tsx'
import { useForm as useFormHandler } from '../../hooks/useForm.ts'
import controlStore from '../../store/controlStore.ts'
import { Section } from './components/Section.tsx'
import { LabeledInput } from './widgets/LabeledInput.tsx'
import { RadioGroup } from './widgets/RadioGroup.tsx'
import { FormLayout } from './components/FormLayout.tsx'
import { SubmitCancelButtons } from './components/SubmitCancelButtons.tsx'
import { IPet } from '../../interfaces/types.ts'
import { LabeledSelect } from './widgets/LabeledSelect.tsx'
import { ColorPicker } from './widgets/ColorPicker.tsx'
import { BirthDate } from './widgets/BirthDate.tsx'
import { Header } from './components/Header.tsx'
import { GenderOptions } from './widgets/options.tsx'
import dataStore from '../../store/dataStore.ts'
import { ImageUpload } from './widgets/ImageUpload.tsx'
import SelectPet2 from '../../components/common/SelectPet/SelectPet2.tsx'
import { PetSelectPopup } from './components/PetSelectPopup.tsx'
import { AlertSettingsButton } from '../../components/common/buttons/AlertSettingsButton.tsx'

export const PetForm: React.FC = () => {
    const location = useLocation()
    const addTrackerData = location?.state?.data
    const speciesList = dataStore(s => s.species)
    const breeds = dataStore(s => s.breeds) || []
    
    // Track newly typed options locally so the LabeledSelect UI works
    const [localSpecies, setLocalSpecies] = React.useState<{speciesName: string, _id: string}[]>([])
    const [localBreeds, setLocalBreeds] = React.useState<{breedName: string, speciesType: string, _id: string}[]>([])

    const speciesOptions = [...speciesList, ...localSpecies].map(b => ({value: b._id, label: b.speciesName}))
    const account = dataStore(s => s.account)
    const isMobile = controlStore(s => s.isOnMobileDevice)
    const pets = dataStore(s => s.pets)
    const [pet, setPet ] = React.useState<IPet | null>(location?.state?.pet as IPet || null)
    const devices = dataStore(s => s.devices)
    const [alertOpen, setAlertOpen] = React.useState(false)

    const birthDate = pet ? typeof pet.birthDate === 'string' ? parseBirthDate(pet.birthDate) : pet.birthDate : ''

    const defaultValues = {
        _id: pet?._id || 'new',
        petName: pet?.petName || '',
        birthDate,
        species: pet?.species || '',
        breed: pet?.breed || '',
        gender: pet?.gender || '',
        petWeight: pet?.petWeight || '',
        imageURL: pet?.imageURL || '',
        colorId: pet?.colorId || '',
        uploadImage: {file: '', img: ''}
    }

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        control,
        trigger,
        watch,
        setValue,
        reset,
        getValues,
    } = useForm({
        defaultValues: defaultValues,
        mode: 'onChange',
        reValidateMode: 'onBlur',
    })

    React.useEffect(() => {
        void trigger()
    }, [])

    // clear breed when species changes (only when species actually changes to a different value)
    const species = watch('species')
    const previousSpeciesRef = React.useRef(species)
    React.useEffect(() => {
        if (species && !/^[a-f\d]{24}$/i.test(species) && !speciesList.find(s => s._id === species) && !localSpecies.find(s => s._id === species)) {
            setLocalSpecies([{
                speciesName: species,
                _id: species,
            }])
        }
        if (previousSpeciesRef.current !== species) {
            setValue('breed', '')
            previousSpeciesRef.current = species
        }
    }, [species, setValue, speciesList, localSpecies])

    const breed = watch('breed')
    React.useEffect(() => {
        if (breed && !/^[a-f\d]{24}$/i.test(breed) && !breeds.find(b => b._id === breed) && !localBreeds.find(b => b._id === breed)) {
            setLocalBreeds([{
                breedName: breed,
                speciesType: species,
                _id: breed,
            }])
        }
    }, [breed, species, breeds, localBreeds])

    const uploadImage = watch('uploadImage')
    const petPhoto = uploadImage?.img || getValues().imageURL || ''

    const navigate = useNavigate()
    const [onSubmit] = useFormHandler()

    const switchPet = (id: string) => {
        const pet = pets.find(pet => pet._id === id)
        setPet(pet || null)
        const _birthDate = pet ? typeof pet.birthDate === 'string' ? parseBirthDate(pet.birthDate) : pet.birthDate : ''

        const update = pet ? {
            _id: pet._id || 'new',
            petName: pet.petName || '',
            birthDate: _birthDate,
            species: pet.species || '',
            breed: pet.breed || '',
            gender: pet.gender || '',
            petWeight: pet.petWeight || '',
            imageURL: pet.imageURL || '',
            colorId: pet.colorId || '',
            uploadImage: {file: '', img: ''}
        } : {
            _id: 'new',
            petName: '',
            birthDate: '',
            species: '',
            breed: '',
            gender: '',
            petWeight: '',
            imageURL: '',
            colorId: '',
            uploadImage: {file: '', img: ''}
        }
        previousSpeciesRef.current = update.species
        reset(update)
    }
    const chooseBreedOptions = (species: string) =>
        [...breeds, ...localBreeds]
            .filter(b => b.speciesType === species)
            .map(d => ({label: d.breedName.charAt(0).toUpperCase() + d.breedName.slice(1), value: d._id}))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))

    const cancel = () => {
        const state = {
            data: {
                ...addTrackerData,
                petId: ''
            },
        }
        navigate('/' + controlStore.getState().currentPage, {state})
    }
    const processForm = async (data: any) => {
        if (alertOpen) return
        
        // Push any custom species and breeds to the global store now that the form is saving
        if (data.species && !speciesList.find(s => s._id === data.species)) {
            dataStore.getState().setSpecies([...speciesList, {
                speciesName: data.species,
                _id: data.species,
            }])
        }
        if (data.breed && !breeds.find(b => b.speciesType === data.species && b._id === data.breed)) {
            dataStore.getState().setBreeds([...breeds, {
                breedName: data.breed,
                speciesType: data.species,
                _id: data.breed,
            }])
        }

        if (addTrackerData) {
            const petId = await createPetAndAssignTracker(addTrackerData.iccid, data)
            const state = { data: {
                ...addTrackerData,
                petId
                }}
            navigate('/' + controlStore.getState().currentPage, {state})
        } else {
            void onSubmit('pet', data)
        }
    }
    
    const width = !isMobile ? 'w-[32rem]' : ''

    const header = <Header title={``}/>

    return (
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        <form onSubmit={handleSubmit(processForm)} className={`${width} h-full mb-6`}>
            <FormLayout trigger={trigger} header={header} top={true}>

                <div className={'flex flex-col gap-2 h-[90%]'}>
                    <div className={'flex flex-col item-center gap-3'}>
                        {!isMobile ? (
                            <Section title={'Select a pet'}>
                                <SelectPet2
                                    onChange={function (p: {
                                        label: string,
                                        value: string
                                    }): void {
                                        switchPet(p.value)
                                    }}
                                    value={pet?._id || 'new'}
                                    disabled={false}
                                    displayOnly={false}
                                />
                            </Section>
                        ) : (
                            <PetSelectPopup
                                onChange={(p: IPet | null) => {
                                    switchPet(p?._id || 'new')
                                }}
                                pet={pet}
                                showNew
                            />
                        )}

                        <Section>
                            <LabeledInput
                                formField={'petName'}
                                title={'Pet Name'}
                                {...register('petName' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                            />
                        </Section>

                        <Section>
                            <LabeledSelect
                                control={control}
                                formField={'species'}
                                title={'Pet Species'}
                                errors={errors}
                                options={speciesOptions}
                                required
                                creatable
                            />

                            <LabeledSelect
                                control={control}
                                formField={'breed'}
                                title={'Pet Breed'}
                                errors={errors}
                                options={chooseBreedOptions(species)}
                                required
                                creatable
                            />
                        </Section>

                        <Section>

                            <BirthDate
                                control={control}
                                formField={'birthDate'}
                                title={'Pets birth date'}
                                errors={errors}
                                back={true}
                                initialVal={birthDate}
                            />

                            <RadioGroup
                                formField={'gender'}
                                control={control}
                                options={GenderOptions}
                                title={'Gender'}
                                errors={errors}
                                required
                            />

                            <LabeledInput
                                formField={'petWeight'}
                                title={'Weight'}
                                {...register('petWeight' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                                scale={account.country ? account.country === 'US' ? 'lbs' : 'kg' : 'lbs'}
                            />

                        </Section>

                        <Section>
                            <ColorPicker
                                control={control}
                                formField={'colorId'}
                                title={'Map Marker Color'}
                                errors={errors}
                                required
                            />

                            <ImageUpload
                                control={control}
                                formField={'uploadImage'}
                                title={'Photo'}
                                errors={errors}
                                imageUrl={petPhoto}
                            />

                        </Section>

                        {devices?.find(d => d.petId === pet?._id) ? <div className={'m-auto'} onClick={() => setAlertOpen(!alertOpen)}>
                            <AlertSettingsButton pet={pet!} outline onClose={() => setTimeout(() => setAlertOpen(false))} />
                        </div> : null}

                    </div>

                    <SubmitCancelButtons errors={errors} cancel={cancel} isCreate={defaultValues._id === 'new'} changed={defaultValues._id !== 'new' ? isDirty : undefined}/>
                    {pet ? <RemovePetButton pet={pet} iconOnly/> : null}
                </div>

            </FormLayout>
        </form>
    )
}
